import { newPurchaseLine } from '../achat/purchaseLines';
import SalesBlockedWarning from './SalesBlockedWarning';
import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Typography,
  Input,
  Divider,
  Tag,
  AutoComplete,
  Spin,
  Tabs,
  Checkbox,
  List,
  Pagination,
  message,
} from 'antd';
import { SearchOutlined, CloseCircleFilled, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import ArticleList from '../article/ArticleList';
import { getAllArticles, getArticleStocks, getArticleById } from '../../models/articleAPI';

const { Text } = Typography;

/**
 * Universal AddItemForm Component
 * 
 * Configuration props:
 * @param {string} refField - Reference field name (e.g., 'REF_DEV', 'REF_AVR', 'REF_FAC', 'REF_BL', 'REF_CA', 'REF_AVR_A', 'REF_BR_A', 'REF_TRF')
 * @param {string} refValue - Value for the reference field
 * @param {function} onSuccess - Callback when items are submitted (receives array of items)
 * @param {string} codeEnt - Optional entrepot code for stock filtering
 * @param {string} priceType - 'selling' (PV_HT/PV_TTC), 'purchase' (PA_HT/PA_TTC), or 'none' (no prices). Default: 'selling'
 * @param {boolean} useNegativeTotals - For avoir (credit notes), use negative totals. Default: false
 * @param {boolean} enableBatch - Enable batch selection mode. Default: true
 * @param {boolean} useArticleList - Use ArticleList component instead of AutoComplete. Default: false
 * @param {string} submitButtonText - Text for submit button. Default: 'Add all to {type}'
 * @param {object} extraFields - Additional fields to include in itemData (e.g., { qte_prepare: -1, qte_controle: -1 })
 * @param {function} onQuantityChange - Optional callback when quantity changes (for special handling like bonliv)
 * @param {string} quantityFieldName - Field name for quantity (default: 'QTE', can be 'QTE_STK' for transfert)
 * @param {boolean} allowNegativeQuantity - Allow negative (non-zero) quantities, e.g. for stock corrections. Default: false
 */
const UniversalAddItemForm = ({
  refField,
  refValue,
  onSuccess,
  codeEnt,
  priceType = 'selling', // 'selling' or 'purchase'
  useNegativeTotals = false,
  enableBatch = true,
  useArticleList = false,
  submitButtonText,
  extraFields = {},
  onQuantityChange: customQuantityHandler,
  quantityFieldName = 'QTE',
  allowNegativeQuantity = false,
  allowDuplicateArticles = false,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const articleSelectionVersion = useRef(0);
  const [itemData, setItemData] = useState({
    [refField]: refValue,
    CODE_ART: '',
    ARTICLE: '',
    [quantityFieldName]: 0,
    GRATUIT: 0,
    PA_HT: 0,
    PA_TTC: 0,
    PV_HT: 0,
    PV_TTC: 0,
    TVA: 0,
    REMISE: 0,
    TotalHT: 0,
    TotalTTC: 0,
    article: {
      uav: '',
      qt_cart: 0,
      q_paquet: 0,
      REF_OEM: '',
      qte_stk: 0,
      Zone: '',
      Etage: '',
    },
    ...extraFields,
  });

  // Shared search query for both views
  const [sharedSearchQuery, setSharedSearchQuery] = useState('');
  
  // Unified selection - shared between Single and Multiple views
  const [unifiedSelection, setUnifiedSelection] = useState([]); // Array of item objects
  
  // Single view state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [lastFetchedArticles, setLastFetchedArticles] = useState([]);
  const quantityInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const [suggestionPage, setSuggestionPage] = useState(1);
  const [suggestionPageSize] = useState(20);
  const [suggestionTotal, setSuggestionTotal] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [articleStocks, setArticleStocks] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  
  // Multiple selection view state
  const [activeTab, setActiveTab] = useState('single');
  const [multipleArticles, setMultipleArticles] = useState([]);
  const [multipleLoading, setMultipleLoading] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState(new Set()); // Set of code_art strings
  const [selectedArticlesData, setSelectedArticlesData] = useState(new Map()); // Map of code_art -> article object
  const [multiplePage, setMultiplePage] = useState(1);
  const [multiplePageSize] = useState(20);
  const [multipleTotal, setMultipleTotal] = useState(0);

  const buildOptionsFromArticles = (articles) => {
    return articles.map(a => {
      // Get stock for the specific entrepot if codeEnt is provided
      let stockQty = 0;
      if (codeEnt && a.stocks) {
        const entrepotStock = a.stocks.find(s => s.code_ent === codeEnt);
        stockQty = entrepotStock?.stock?.qte_stk ?? 0;
      } else {
        stockQty = a.stocks?.[0]?.stock?.qte_stk ?? a.stocks?.[0]?.qte_stk ?? a.qte_stk ?? 0;
      }

      return {
        value: a.code_art,
        label: (
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr auto auto', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 600 }}>{a.code_art}</div>
            <div style={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.ARTICLE}</div>
            <div>
              <Tag color="default">Stock {stockQty}</Tag>
            </div>
            <div>
              {priceType === 'purchase' ? (
                <Tag color="blue">{Number(a.PA_HT ?? 0).toFixed(2)} HT</Tag>
              ) : (
                <Tag color="blue">{Number(a.PV_TTC ?? 0).toFixed(2)}</Tag>
              )}
            </div>
          </div>
        )
      };
    });
  };

  const fetchSuggestions = async ({ page, append }) => {
    try {
      setSuggestionsLoading(true);
      const params = {
        page,
        pageSize: suggestionPageSize,
        codeArtSearch: sharedSearchQuery,
        sortField: 'code_art',
        sortOrder: 'asc'
      };
      const data = await getAllArticles(params);
      const fetched = Array.isArray(data?.articles) ? data.articles : [];
      const total = Number(data?.total ?? 0);

      const newArticles = append ? [...lastFetchedArticles, ...fetched] : fetched;
      setLastFetchedArticles(newArticles);

      const builtOpts = buildOptionsFromArticles(newArticles);
      setSuggestions(builtOpts);
      setSuggestionTotal(total);
      setSuggestionPage(page);
    } catch (e) {
      setSuggestions([]);
      setLastFetchedArticles([]);
      setSuggestionTotal(0);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'single') {
      if (!sharedSearchQuery || sharedSearchQuery.trim().length < 2) {
        setSuggestions([]);
        setLastFetchedArticles([]);
        setSuggestionTotal(0);
        setSuggestionPage(1);
        setDropdownOpen(false);
        return;
      }
      const timeoutId = setTimeout(() => {
        setDropdownOpen(true);
        fetchSuggestions({ page: 1, append: false });
      }, 250);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedSearchQuery, activeTab]);

  const calculateTotals = (data) => {
    const quantity = data[quantityFieldName] || 0;
    
    // No price calculations for transfert and similar
    if (priceType === 'none') {
      return {
        TotalHT: 0,
        TotalTTC: 0
      };
    }
    
    const remise = data.REMISE || 0;
    const tva = data.TVA || 0;

    if (priceType === 'purchase') {
      const paHT = data.PA_HT || 0;
      const paTTC = paHT * (1 + tva / 100);
      const totalHT = quantity * paHT;
      const totalTTC = quantity * paTTC;
      const multiplier = useNegativeTotals ? -1 : 1;

      return {
        PA_TTC: paTTC,
        TotalHT: totalHT * multiplier,
        TotalTTC: totalTTC * multiplier
      };
    } else {
      const pvTTC = data.PV_TTC || 0;
      const pvHT = pvTTC / (1 + tva / 100);
      const totalTTC = quantity * pvTTC * (1 - remise / 100);
      const totalHT = quantity * pvHT * (1 - remise / 100);
      const multiplier = useNegativeTotals ? -1 : 1;

      return {
        PV_HT: pvHT,
        TotalHT: totalHT * multiplier,
        TotalTTC: totalTTC * multiplier
      };
    }
  };

  const handleSelectArticle = async (article) => {
    const selectionVersion = ++articleSelectionVersion.current;
    setSelectedItem(null);
    if (priceType === 'selling') {
      try {
        const { data } = await getArticleById(article.code_art);
        if (selectionVersion !== articleSelectionVersion.current) return;
        // Refresh the flag before the article can be added, even if search results are older.
        article = { ...article, VENTE_BLOQ: data.VENTE_BLOQ };
      } catch (error) {
        if (selectionVersion === articleSelectionVersion.current) {
          message.error('Impossible de vérifier cet article. Veuillez le sélectionner à nouveau.');
        }
        return;
      }
    }
    setSelectedItem(article);

    // Get stock from stocks array if available (for current entrepot)
    let stockData = null;
    let stockQty = 0;

    if (codeEnt && article.stocks) {
      // Find stock for the specific entrepot
      const entrepotStock = article.stocks.find(s => s.code_ent === codeEnt);
      if (entrepotStock) {
        stockData = entrepotStock.stock;
        stockQty = stockData?.qte_stk ?? 0;
      }
    } else if (article.stocks && article.stocks.length > 0) {
      // Fallback to first stock
      stockData = article.stocks[0].stock || article.stocks[0];
      stockQty = stockData?.qte_stk ?? article.qte_stk ?? 0;
    } else {
      stockQty = article.qte_stk ?? 0;
    }

    const newItemData = {
      ...itemData,
      CODE_ART: article.code_art,
      ARTICLE: article.ARTICLE,
      PV_TTC: article.PV_TTC || 0,
      PV_HT: article.PV_HT || 0,
      PA_HT: article.PA_HT || 0,
      TVA: article.TVA || 20,
      REMISE: parseFloat(article.Remise) || 0,
      article: {
        VENTE_BLOQ: article.VENTE_BLOQ === true,
        uav: article.uav || '',
        qt_cart: article.qt_cart || 0,
        q_paquet: article.q_paquet || 0,
        REF_OEM: article.REF_OEM || '',
        qte_stk: stockQty,
        Zone: stockData?.Zone || '',
        Etage: stockData?.Etage || '',
        stocks: article.stocks || [] // Store all stocks for reference
      }
    };

    const totals = calculateTotals(newItemData);
    setItemData({
      ...newItemData,
      ...totals
    });

    // Fetch all stocks for this article across all entrepôts
    if (article.code_art) {
      setLoadingStocks(true);
      try {
        const stocks = await getArticleStocks(article.code_art);
        setArticleStocks(stocks);
      } catch (error) {
        console.error('Error fetching article stocks:', error);
        setArticleStocks([]);
      } finally {
        setLoadingStocks(false);
      }
    }

    setTimeout(() => {
      if (quantityInputRef.current && typeof quantityInputRef.current.focus === 'function') {
        quantityInputRef.current.focus();
      }
      if (quantityInputRef.current && typeof quantityInputRef.current.select === 'function') {
        quantityInputRef.current.select();
      }
    }, 0);
  };

  const handleQuantityChange = (value) => {
    // Keep intermediate inputs like "-" so users can type negative numbers
    if (allowNegativeQuantity && (value === '' || value === '-')) {
      setItemData({ ...itemData, [quantityFieldName]: value });
      return;
    }
    const numericValue = parseFloat(value) || 0;
    const newData = { ...itemData, [quantityFieldName]: numericValue };
    
    // Apply custom quantity handler if provided (e.g., for bonliv)
    if (customQuantityHandler) {
      customQuantityHandler(newData, setItemData, calculateTotals);
      return;
    }
    
    const totals = calculateTotals(newData);
    setItemData({ ...newData, ...totals });
  };

  const handleReset = () => {
    const priceValue = priceType === 'purchase' 
      ? (selectedItem?.PA_HT || 0)
      : (selectedItem?.PV_TTC || 0);
    
    setItemData({
      ...itemData,
      [quantityFieldName]: 0,
      REMISE: selectedItem?.Remise ? parseFloat(selectedItem.Remise) : 0,
      PV_TTC: priceType === 'selling' ? priceValue : itemData.PV_TTC,
      PA_HT: priceType === 'purchase' ? priceValue : itemData.PA_HT,
      PV_HT: 0,
      TotalHT: 0,
      TotalTTC: 0,
    });
  };

  const buildCurrentItem = () => {
    if (!selectedItem) return null;
    const totals = calculateTotals(itemData);
    return {
      ...itemData,
      article: { ...itemData.article, VENTE_BLOQ: selectedItem.VENTE_BLOQ === true },
      [refField]: refValue,
      PV_HT: totals.PV_HT,
      PA_TTC: totals.PA_TTC,
      TotalHT: totals.TotalHT || 0,
      TotalTTC: totals.TotalTTC || 0
    };
  };

  const handleAddToSelection = () => {
    const submitData = buildCurrentItem();
    if (!submitData) return;
    
    const qty = parseFloat(submitData[quantityFieldName]) || 0;
    const price = priceType === 'purchase' ? submitData.PA_HT : (priceType === 'none' ? 1 : submitData.PV_TTC);
    
    const invalidQty = allowNegativeQuantity ? qty === 0 : qty <= 0;
    if (invalidQty || (priceType !== 'none' && price <= 0)) return;
    submitData[quantityFieldName] = qty;
    
    // Add to unified selection
    setUnifiedSelection((prev) => {
      if (allowDuplicateArticles) return [...prev, newPurchaseLine(submitData)];
      const existingIndex = prev.findIndex((p) => p.CODE_ART === submitData.CODE_ART);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = submitData;
        return updated;
      }
      return [...prev, submitData];
    });
    
    // Reset form
    setItemData({
      ...itemData,
      [quantityFieldName]: 0,
      REMISE: selectedItem?.Remise ? parseFloat(selectedItem.Remise) : 0,
      PV_TTC: selectedItem?.PV_TTC || 0,
      PA_HT: selectedItem?.PA_HT || 0,
      PV_HT: 0,
      TotalHT: 0,
      TotalTTC: 0,
    });
    setSelectedItem(null);
    setArticleStocks([]);
    setTimeout(() => {
      if (searchInputRef.current && typeof searchInputRef.current.focus === 'function') {
        searchInputRef.current.focus();
      }
      if (searchInputRef.current && typeof searchInputRef.current.select === 'function') {
        searchInputRef.current.select();
      }
      setDropdownOpen(true);
    }, 0);
  };

  const handleSubmitAll = () => {
    if (unifiedSelection.length === 0) return;
    onSuccess(unifiedSelection);
    setUnifiedSelection([]);
    setSelectedItem(null);
    setSelectedArticles(new Set());
    setSelectedArticlesData(new Map());
    setItemData({
      ...itemData,
      [quantityFieldName]: 0,
      REMISE: 0,
      PV_TTC: 0,
      PA_HT: 0,
      PV_HT: 0,
      TotalHT: 0,
      TotalTTC: 0,
    });
    setTimeout(() => {
      if (searchInputRef.current && typeof searchInputRef.current.focus === 'function') {
        searchInputRef.current.focus();
      }
      if (searchInputRef.current && typeof searchInputRef.current.select === 'function') {
        searchInputRef.current.select();
      }
      setDropdownOpen(true);
    }, 0);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Enter') {
        handleAddToSelection();
      }
      if (e.key === 'Escape') {
        if (selectedItem) setSelectedItem(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedItem, itemData]);

  const qty = parseFloat(itemData[quantityFieldName]) || 0;
  const price = priceType === 'purchase' ? itemData.PA_HT : (priceType === 'none' ? 1 : itemData.PV_TTC);
  const isValid = selectedItem && (allowNegativeQuantity ? qty !== 0 : qty > 0) && (priceType === 'none' || price > 0);

  const defaultButtonText = submitButtonText || `Add all to ${refField.replace('REF_', '')}`;

  // Fetch articles for multiple selection view
  const fetchMultipleArticles = async (page) => {
    try {
      setMultipleLoading(true);
      const params = {
        page,
        pageSize: multiplePageSize,
        codeArtSearch: sharedSearchQuery,
        sortField: 'code_art',
        sortOrder: 'asc'
      };
      const data = await getAllArticles(params);
      const fetched = Array.isArray(data?.articles) ? data.articles : [];
      const total = Number(data?.total ?? 0);

      setMultipleArticles(fetched);
      setMultipleTotal(total);
      setMultiplePage(page);
    } catch (e) {
      setMultipleArticles([]);
      setMultipleTotal(0);
    } finally {
      setMultipleLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'multiple') {
      if (!sharedSearchQuery || sharedSearchQuery.trim().length < 2) {
        setMultipleArticles([]);
        setMultipleTotal(0);
        setMultiplePage(1);
        return;
      }
      const timeoutId = setTimeout(() => {
        fetchMultipleArticles(1);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedSearchQuery, activeTab]);

  // Fetch when page changes in multiple view
  useEffect(() => {
    if (activeTab === 'multiple' && sharedSearchQuery && sharedSearchQuery.trim().length >= 2 && multiplePage > 1) {
      fetchMultipleArticles(multiplePage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiplePage]);

  const handleToggleArticle = (article) => {
    const codeArt = article.code_art;
    setSelectedArticles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(codeArt)) {
        newSet.delete(codeArt);
        setSelectedArticlesData((prevData) => {
          const newMap = new Map(prevData);
          newMap.delete(codeArt);
          return newMap;
        });
      } else {
        newSet.add(codeArt);
        setSelectedArticlesData((prevData) => {
          const newMap = new Map(prevData);
          newMap.set(codeArt, article);
          return newMap;
        });
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allSelected = selectedArticles.size === multipleArticles.length && multipleArticles.length > 0;
    if (allSelected) {
      setSelectedArticles(new Set());
      setSelectedArticlesData(new Map());
    } else {
      setSelectedArticles(new Set(multipleArticles.map(a => a.code_art)));
      setSelectedArticlesData((prevData) => {
        const newMap = new Map(prevData);
        multipleArticles.forEach(article => {
          newMap.set(article.code_art, article);
        });
        return newMap;
      });
    }
  };

  const handleAddMultipleToSelection = () => {
    if (selectedArticles.size === 0) return;

    // Use selectedArticlesData to get full article objects, regardless of current page
    const selectedArticlesList = Array.from(selectedArticlesData.values());

    const itemsToAdd = selectedArticlesList.map(article => {
        // Get stock data for the specific entrepot
        let stockData = null;
        let stockQty = 0;

        if (codeEnt && article.stocks) {
          const entrepotStock = article.stocks.find(s => s.code_ent === codeEnt);
          if (entrepotStock) {
            stockData = entrepotStock.stock;
            stockQty = stockData?.qte_stk ?? 0;
          }
        } else if (article.stocks && article.stocks.length > 0) {
          stockData = article.stocks[0].stock || article.stocks[0];
          stockQty = stockData?.qte_stk ?? article.qte_stk ?? 0;
        } else {
          stockQty = article.qte_stk ?? 0;
        }

        const baseItem = {
          [refField]: refValue,
          CODE_ART: article.code_art,
          ARTICLE: article.ARTICLE,
          [quantityFieldName]: 1, // Default quantity
          GRATUIT: 0,
          PA_HT: article.PA_HT || 0,
          PA_TTC: 0,
          PV_HT: article.PV_HT || 0,
          PV_TTC: article.PV_TTC || 0,
          TVA: article.TVA || 20,
          REMISE: parseFloat(article.Remise) || 0,
          article: {
            VENTE_BLOQ: article.VENTE_BLOQ === true,
            uav: article.uav || '',
            qt_cart: article.qt_cart || 0,
            q_paquet: article.q_paquet || 0,
            REF_OEM: article.REF_OEM || '',
            qte_stk: stockQty,
            Zone: stockData?.Zone || '',
            Etage: stockData?.Etage || '',
            stocks: article.stocks || [] // Store all stocks for reference
          },
          ...extraFields,
        };

        const totals = calculateTotals(baseItem);
        return {
          ...baseItem,
          PV_HT: totals.PV_HT,
          PA_TTC: totals.PA_TTC,
          TotalHT: totals.TotalHT || 0,
          TotalTTC: totals.TotalTTC || 0
        };
      });

    // Add to unified selection instead of submitting directly
    setUnifiedSelection((prev) => {
      if (allowDuplicateArticles) return [...prev, ...itemsToAdd.map(newPurchaseLine)];
      const updated = [...prev];
      itemsToAdd.forEach(item => {
        const existingIndex = updated.findIndex(p => p.CODE_ART === item.CODE_ART);
        if (existingIndex !== -1) {
          updated[existingIndex] = item;
        } else {
          updated.push(item);
        }
      });
      return updated;
    });

    // Clear multiple selection but keep search query
    setSelectedArticles(new Set());
    setSelectedArticlesData(new Map());
  };

  // Get selected articles from stored data, not just current page
  const selectedArticlesList = Array.from(selectedArticlesData.values());

  return (
    <div>
      <style>{`
        .topbar { position: sticky; top: 0; z-index: 2; background: #fff; padding: 8px 0; }
        .config-section { margin-bottom: 16px; }
        .metric { display: flex; justify-content: space-between; font-weight: 600; }
        .hint { color: #8c8c8c; font-size: 12px; }
        .pill { display: inline-flex; align-items: center; gap: 8px; border: 1px solid #d9d9d9; border-radius: 999px; padding: 4px 10px; background: #fafafa; }
        .article-list-item { padding: 12px; border-bottom: 1px solid #f0f0f0; cursor: pointer; }
        .article-list-item:hover { background: #fafafa; }
        .article-list-item.selected { background: #e6f7ff; }
      `}</style>

      {/* Persistent search bar - shared between both views */}
      <div className="topbar" style={{ marginBottom: 16, position: 'relative' }}>
        <AutoComplete
          style={{ width: '100%' }}
          options={activeTab === 'single' ? suggestions : []}
          open={activeTab === 'single' && dropdownOpen}
          onSelect={(value) => {
            const found = lastFetchedArticles.find(a => a.code_art === value);
            if (found) {
              handleSelectArticle(found);
              setDropdownOpen(false);
            }
          }}
          dropdownMatchSelectWidth={600}
          notFoundContent={suggestionsLoading ? <Spin size="small" /> : null}
          onBlur={() => setDropdownOpen(false)}
          onFocus={() => {
            if (activeTab === 'single' && sharedSearchQuery && sharedSearchQuery.trim().length >= 2) {
              setDropdownOpen(true);
            }
          }}
          dropdownRender={(menu) => {
            const hasMore = (suggestionPage * suggestionPageSize) < suggestionTotal;
            return (
              <div>
                {menu}
                {hasMore && (
                  <div
                    style={{ padding: '6px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.preventDefault();
                        const nextPage = suggestionPage + 1;
                        fetchSuggestions({ page: nextPage, append: true });
                        setDropdownOpen(true);
                      }}
                    >
                      Load more (+{suggestionPageSize})
                    </Button>
                  </div>
                )}
              </div>
            );
          }}
        >
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search by barcode or article code"
            value={sharedSearchQuery}
            onChange={(e) => {
              setSharedSearchQuery(e.target.value);
              if (activeTab === 'single' && e.target.value && e.target.value.trim().length >= 2) {
                setDropdownOpen(true);
              } else {
                setDropdownOpen(false);
              }
            }}
            size="large"
            ref={searchInputRef}
          />
        </AutoComplete>
        {activeTab === 'single' && selectedItem && (
          <div style={{ marginTop: 8 }}>
            <div className="pill">
              <span 
                style={{ 
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px',
                  display: 'inline-block'
                }}
                title={selectedItem.ARTICLE}
              >
                {selectedItem.ARTICLE}
              </span>
              {priceType === 'selling' && <SalesBlockedWarning item={{ article: selectedItem }} />}
              <Tag color="blue">{selectedItem.code_art}</Tag>
              <Button
                size="small"
                type="text"
                icon={<CloseCircleFilled />}
                onClick={() => {
                  setSelectedItem(null);
                  setArticleStocks([]);
                }}
              />
            </div>
          </div>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'single',
            label: 'Single',
            children: (
              <div>

      <div className="config-section">
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Card size="small" bordered>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text 
                  strong 
                  style={{ 
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                  title={selectedItem ? selectedItem.ARTICLE : 'No article selected'}
                >
                  {selectedItem ? selectedItem.ARTICLE : 'No article selected'}{' '}
                  {priceType === 'selling' && selectedItem && <SalesBlockedWarning item={{ article: selectedItem }} />}
                </Text>
                {selectedItem ? (
                  <Space size={8} wrap>
                    <Tag color="blue">Code: {selectedItem.code_art}</Tag>
                    <Tag>{itemData.article.uav || 'UAV: N/A'}</Tag>
                    <Tag color="purple">TVA {itemData.TVA}%</Tag>
                    {loadingStocks ? (
                      <Tag><Spin size="small" /></Tag>
                    ) : articleStocks.length > 0 ? (
                      // If codeEnt is specified, prioritize showing that entrepot first
                      codeEnt ? (
                        <>
                          {articleStocks
                            .filter(entrepot => entrepot.code_ent === codeEnt)
                            .map((entrepot) => (
                              <Tag
                                key={entrepot.code_ent}
                                color={entrepot.stock && entrepot.stock.qte_stk > 0 ? 'green' : 'red'}
                              >
                                Ent {entrepot.name} ({entrepot.code_ent}): stk {entrepot.stock ? entrepot.stock.qte_stk : 0}
                              </Tag>
                            ))}
                          {articleStocks
                            .filter(entrepot => entrepot.code_ent !== codeEnt)
                            .map((entrepot) => (
                              <Tag
                                key={entrepot.code_ent}
                                color={entrepot.stock && entrepot.stock.qte_stk > 0 ? 'blue' : 'default'}
                              >
                                Ent {entrepot.name} ({entrepot.code_ent}): stk {entrepot.stock ? entrepot.stock.qte_stk : 0}
                              </Tag>
                            ))}
                        </>
                      ) : (
                        articleStocks.map((entrepot) => (
                          <Tag
                            key={entrepot.code_ent}
                            color={entrepot.stock && entrepot.stock.qte_stk > 0 ? 'green' : 'default'}
                          >
                            Ent {entrepot.name} ({entrepot.code_ent}): stk {entrepot.stock ? entrepot.stock.qte_stk : 0}
                          </Tag>
                        ))
                      )
                    ) : (
                      <Tag color="green">Stock: {itemData.article.qte_stk || 0}</Tag>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">Select an article {useArticleList ? 'from the table below' : 'using the search above'}</Text>
                )}
              </Space>
            </Card>
          </Col>

          <Col span={8}>
            <Card size="small" title="Quantity & Pricing" bordered>
              <Row gutter={[8, 8]}>
                <Col span={8}>
                  <Text type="secondary">Quantity</Text>
                  <Input
                    style={{ width: '100%' }}
                    ref={quantityInputRef}
                    value={itemData[quantityFieldName].toString()}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="Enter quantity"
                    disabled={!selectedItem}
                  />
                </Col>
                {priceType !== 'none' && (
                  <>
                    <Col span={8}>
                      <Text type="secondary">{priceType === 'purchase' ? 'Price HT' : 'Price TTC'}</Text>
                      <Input
                        style={{ width: '100%' }}
                        value={priceType === 'purchase' ? itemData.PA_HT.toString() : itemData.PV_TTC.toString()}
                        placeholder={`Price from article`}
                        disabled
                        readOnly
                      />
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">Remise (%)</Text>
                      <Input
                        style={{ width: '100%' }}
                        value={itemData.REMISE.toString()}
                        placeholder="Discount from article"
                        disabled
                        readOnly
                      />
                    </Col>
                  </>
                )}
                {priceType === 'none' && <Col span={16} />}
              </Row>
            </Card>
          </Col>

          <Col span={8}>
            <Card size="small" bordered>
              <Space direction="vertical" style={{ width: '100%' }}>
                {priceType !== 'none' && (
                  <>
                    <Row justify="space-between" className="metric">
                      <Text>Total HT:</Text>
                      <Text>{Math.abs(Number(itemData.TotalHT)).toFixed(2)}</Text>
                    </Row>
                    <Row justify="space-between" className="metric">
                      <Text>Total TTC:</Text>
                      <Text>{Math.abs(Number(itemData.TotalTTC)).toFixed(2)}</Text>
                    </Row>
                  </>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <Space style={{ width: '100%' }}>
                  <Button onClick={handleReset} style={{ flex: 1 }} disabled={!selectedItem}>Reset</Button>
                  <Button
                    type="primary"
                    onClick={handleAddToSelection}
                    style={{ flex: 2 }}
                    disabled={!isValid}
                  >
                    Add to selection
                  </Button>
                </Space>
                <div className="hint">Press Enter to add to selection, Esc to clear selection</div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Unified Selection Card - visible in Single view */}
      <div className="config-section">
        <Card size="small" title={`Selection (${unifiedSelection.length})`} bordered>
          {unifiedSelection.length === 0 ? (
            <Text type="secondary">No items in selection. Pick an article, set quantity, then add to selection.</Text>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: priceType === 'none' ? '1fr 4fr 1fr auto' : '1fr 4fr 1fr 1fr 1fr 1fr auto', gap: 8, fontWeight: 600, marginBottom: 6 }}>
                <div>Code</div>
                <div>Article</div>
                <div>Qté</div>
                {priceType !== 'none' && (
                  <>
                    <div>{priceType === 'purchase' ? 'Prix HT' : 'Prix TTC'}</div>
                    <div>Remise (%)</div>
                    <div>Total</div>
                  </>
                )}
                <div>Actions</div>
              </div>
              {unifiedSelection.map((it) => (
                <div key={it.id ?? it.CODE_ART} style={{ display: 'grid', gridTemplateColumns: priceType === 'none' ? '1fr 4fr 1fr auto' : '1fr 4fr 1fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid #f0f0f0' }}>
                  <div><Tag color="blue">{it.CODE_ART}</Tag></div>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.ARTICLE}{' '}{priceType === 'selling' && <SalesBlockedWarning item={it} />}</div>
                  <div>{it[quantityFieldName]}</div>
                  {priceType !== 'none' && (
                    <>
                      <div>{priceType === 'purchase' ? Number(it.PA_HT).toFixed(2) : Number(it.PV_TTC).toFixed(2)}</div>
                      <div>{Number(it.REMISE ?? 0).toFixed(2)}%</div>
                      <div>{Math.abs(Number(it.TotalTTC)).toFixed(2)}</div>
                    </>
                  )}
                  {priceType === 'none' && (
                    <>
                      <div>-</div>
                      <div>-</div>
                      <div>-</div>
                    </>
                  )}
                  <div>
                    <Button 
                      size="small" 
                      type="text"
                      danger 
                      icon={<DeleteOutlined />} 
                      aria-label="Remove"
                      onClick={() => setUnifiedSelection((prev) => prev.filter(p => allowDuplicateArticles ? p.id !== it.id : p.CODE_ART !== it.CODE_ART))}
                    />
                  </div>
                </div>
              ))}
              <Divider style={{ margin: '8px 0' }} />
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setUnifiedSelection([])} disabled={unifiedSelection.length === 0}>Clear</Button>
                <Button type="primary" onClick={handleSubmitAll} disabled={unifiedSelection.length === 0}>{defaultButtonText}</Button>
              </Space>
            </div>
          )}
        </Card>
      </div>

      {useArticleList && (
        <div>
          <ArticleList onSelectArticle={handleSelectArticle} externalQuery={sharedSearchQuery} />
        </div>
      )}
              </div>
            ),
          },
          {
            key: 'multiple',
            label: 'Multiple',
            children: (
              <div>
                {sharedSearchQuery && sharedSearchQuery.trim().length >= 2 && (
                  <>
                    <Card 
                      size="small" 
                      title={
                        <Space>
                          <span>Articles ({multipleTotal})</span>
                          {multipleArticles.length > 0 && (
                            <Checkbox
                              checked={selectedArticles.size === multipleArticles.length && multipleArticles.length > 0}
                              indeterminate={selectedArticles.size > 0 && selectedArticles.size < multipleArticles.length}
                              onChange={handleSelectAll}
                            >
                              Select All ({selectedArticles.size})
                            </Checkbox>
                          )}
                        </Space>
                      }
                      bordered
                      style={{ marginBottom: 16 }}
                    >
                      {multipleLoading ? (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                          <Spin />
                        </div>
                      ) : multipleArticles.length === 0 ? (
                        <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 20 }}>
                          No articles found. Search by barcode or article code.
                        </Text>
                      ) : (
                        <List
                          dataSource={multipleArticles}
                          renderItem={(article) => {
                            const isSelected = selectedArticles.has(article.code_art);
                            const articleStocks = article.stocks || [];

                            return (
                              <List.Item
                                className={`article-list-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleToggleArticle(article)}
                                style={{ padding: '12px 16px' }}
                              >
                                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                  <Space>
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleToggleArticle(article)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <div>
                                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                        {article.code_art}
                                      </div>
                                      <div style={{ color: '#666', fontSize: '12px' }}>
                                        {article.ARTICLE}
                                      </div>
                                    </div>
                                  </Space>
                                  <Space size={4} wrap style={{ flexShrink: 0 }}>
                                    {articleStocks.length > 0 ? (
                                      codeEnt ? (
                                        // Show current entrepot stock first if codeEnt is specified
                                        <>
                                          {articleStocks
                                            .filter((entrepotStock) => entrepotStock.code_ent === codeEnt)
                                            .map((entrepotStock) => (
                                              <Tag
                                                key={entrepotStock.code_ent}
                                                color={entrepotStock.stock && entrepotStock.stock.qte_stk > 0 ? 'green' : 'red'}
                                              >
                                                {entrepotStock.code_ent} ({entrepotStock.name}): {entrepotStock.stock ? entrepotStock.stock.qte_stk : 0}
                                              </Tag>
                                            ))}
                                          {articleStocks
                                            .filter((entrepotStock) => entrepotStock.code_ent !== codeEnt && entrepotStock.stock && entrepotStock.stock.qte_stk > 0)
                                            .map((entrepotStock) => (
                                              <Tag
                                                key={entrepotStock.code_ent}
                                                color="blue"
                                              >
                                                {entrepotStock.code_ent} ({entrepotStock.name}): {entrepotStock.stock.qte_stk}
                                              </Tag>
                                            ))}
                                        </>
                                      ) : (
                                        articleStocks
                                          .filter((entrepotStock) => entrepotStock.stock && entrepotStock.stock.qte_stk > 0)
                                          .map((entrepotStock) => (
                                            <Tag
                                              key={entrepotStock.code_ent}
                                              color="green"
                                            >
                                              {entrepotStock.code_ent} ({entrepotStock.name}): {entrepotStock.stock.qte_stk}
                                            </Tag>
                                          ))
                                      )
                                    ) : null}
                                  </Space>
                                </Space>
                              </List.Item>
                            );
                          }}
                        />
                      )}
                      {multipleTotal > 0 && (
                        <div style={{ marginTop: 16, textAlign: 'center' }}>
                          <Pagination
                            current={multiplePage}
                            total={multipleTotal}
                            pageSize={multiplePageSize}
                            onChange={(page) => setMultiplePage(page)}
                            showSizeChanger={false}
                            showQuickJumper
                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} articles`}
                          />
                        </div>
                      )}
                    </Card>

                    {selectedArticlesList.length > 0 && (
                      <Card 
                        size="small" 
                        title={`Selected Items (${selectedArticlesList.length})`}
                        bordered
                        style={{ marginBottom: 16 }}
                      >
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {selectedArticlesList.map((article) => {
                            const articleStocks = article.stocks || [];

                            return (
                              <div 
                                key={article.code_art}
                                style={{ 
                                  padding: '8px 0',
                                  borderBottom: '1px solid #f0f0f0'
                                }}
                              >
                                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                  <Space>
                                    <Tag color="blue">{article.code_art}</Tag>
                                    <Text>{article.ARTICLE}</Text>
                                  </Space>
                                  <Space size={4} wrap style={{ flexShrink: 0 }}>
                                    {articleStocks.length > 0 ? (
                                      codeEnt ? (
                                        // Show current entrepot stock first if codeEnt is specified
                                        <>
                                          {articleStocks
                                            .filter((entrepotStock) => entrepotStock.code_ent === codeEnt)
                                            .map((entrepotStock) => (
                                              <Tag
                                                key={entrepotStock.code_ent}
                                                color={entrepotStock.stock && entrepotStock.stock.qte_stk > 0 ? 'green' : 'red'}
                                                style={{ fontSize: '11px' }}
                                              >
                                                {entrepotStock.code_ent}: {entrepotStock.stock ? entrepotStock.stock.qte_stk : 0}
                                              </Tag>
                                            ))}
                                          {articleStocks
                                            .filter((entrepotStock) => entrepotStock.code_ent !== codeEnt && entrepotStock.stock && entrepotStock.stock.qte_stk > 0)
                                            .map((entrepotStock) => (
                                              <Tag
                                                key={entrepotStock.code_ent}
                                                color="blue"
                                                style={{ fontSize: '11px' }}
                                              >
                                                {entrepotStock.code_ent}: {entrepotStock.stock.qte_stk}
                                              </Tag>
                                            ))}
                                        </>
                                      ) : (
                                        articleStocks
                                          .filter((entrepotStock) => entrepotStock.stock && entrepotStock.stock.qte_stk > 0)
                                          .map((entrepotStock) => (
                                            <Tag
                                              key={entrepotStock.code_ent}
                                              color="green"
                                              style={{ fontSize: '11px' }}
                                            >
                                              {entrepotStock.code_ent}: {entrepotStock.stock.qte_stk}
                                            </Tag>
                                          ))
                                      )
                                    ) : null}
                                  </Space>
                                </Space>
                              </div>
                            );
                          })}
                        </div>
                        <Divider style={{ margin: '12px 0' }} />
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                          <Button onClick={() => {
                            setSelectedArticles(new Set());
                            setSelectedArticlesData(new Map());
                          }}>
                            Clear Selection
                          </Button>
                          <Button 
                            type="primary" 
                            icon={<CheckOutlined />}
                            onClick={handleAddMultipleToSelection}
                            disabled={selectedArticles.size === 0}
                          >
                            Add {selectedArticles.size} {selectedArticles.size === 1 ? 'Item' : 'Items'} to Selection
                          </Button>
                        </Space>
                      </Card>
                    )}
                  </>
                )}

                {(!sharedSearchQuery || sharedSearchQuery.trim().length < 2) && (
                  <Card bordered>
                    <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 40 }}>
                      Enter a barcode or article code to search for articles
                    </Text>
                  </Card>
                )}

                {/* Unified Selection Card - visible in Multiple view */}
                <div className="config-section" style={{ marginTop: 16 }}>
                  <Card size="small" title={`Selection (${unifiedSelection.length})`} bordered>
                    {unifiedSelection.length === 0 ? (
                      <Text type="secondary">No items in selection. Pick an article, set quantity, then add to selection.</Text>
                    ) : (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: priceType === 'none' ? '1fr 4fr 1fr auto' : '1fr 4fr 1fr 1fr 1fr 1fr auto', gap: 8, fontWeight: 600, marginBottom: 6 }}>
                          <div>Code</div>
                          <div>Article</div>
                          <div>Qté</div>
                          {priceType !== 'none' && (
                            <>
                              <div>{priceType === 'purchase' ? 'Prix HT' : 'Prix TTC'}</div>
                              <div>Remise (%)</div>
                              <div>Total</div>
                            </>
                          )}
                          <div>Actions</div>
                        </div>
                        {unifiedSelection.map((it) => (
                          <div key={it.id ?? it.CODE_ART} style={{ display: 'grid', gridTemplateColumns: priceType === 'none' ? '1fr 4fr 1fr auto' : '1fr 4fr 1fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid #f0f0f0' }}>
                            <div><Tag color="blue">{it.CODE_ART}</Tag></div>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.ARTICLE}{' '}{priceType === 'selling' && <SalesBlockedWarning item={it} />}</div>
                            <div>{it[quantityFieldName]}</div>
                            {priceType !== 'none' && (
                              <>
                                <div>{priceType === 'purchase' ? Number(it.PA_HT).toFixed(2) : Number(it.PV_TTC).toFixed(2)}</div>
                                <div>{Number(it.REMISE ?? 0).toFixed(2)}%</div>
                                <div>{Math.abs(Number(it.TotalTTC)).toFixed(2)}</div>
                              </>
                            )}
                            {priceType === 'none' && (
                              <>
                                <div>-</div>
                                <div>-</div>
                                <div>-</div>
                              </>
                            )}
                            <div>
                              <Button 
                                size="small" 
                                type="text"
                                danger 
                                icon={<DeleteOutlined />} 
                                aria-label="Remove"
                                onClick={() => setUnifiedSelection((prev) => prev.filter(p => allowDuplicateArticles ? p.id !== it.id : p.CODE_ART !== it.CODE_ART))}
                              />
                            </div>
                          </div>
                        ))}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                          <Button onClick={() => setUnifiedSelection([])} disabled={unifiedSelection.length === 0}>Clear</Button>
                          <Button type="primary" onClick={handleSubmitAll} disabled={unifiedSelection.length === 0}>{defaultButtonText}</Button>
                        </Space>
                      </div>
                    )}
                  </Card>
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default UniversalAddItemForm;
